import React from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Flex, Input, Select, Text } from '@chakra-ui/react';
import { DateRangePicker } from 'react-date-range';
import { customStaticRanges } from 'utils/StaticData';
import { enGB } from 'date-fns/locale';
import * as dateFns from 'date-fns';
import { FaChevronDown } from 'react-icons/fa';
import { useDatePickerPosition } from 'hooks/useDatePickerPosition';

/**
 * RAT-1760 AC-6 filters: record, user, route, time range and outcome.
 *
 * Only the routes that actually write entries are offered. bulk_email belongs to
 * RAT-1714 and the HubSpot routes to RAT-1382/1388/1932/1933; listing them before
 * they can produce a single row would just be filters that always come back
 * empty.
 *
 * `migration` is deliberately absent too. The RAT-1474 sheet turns out to carry a
 * populated Source column on every one of its 2,559 rows, so imported history
 * keeps its real route (Manual / CSV) and is marked by `is_migrated` instead -
 * flattening it to a `migration` route would throw away information AC-3 asks to
 * record. If Phase 3 wants those rows filterable, it should add a migrated-history
 * toggle rather than a route.
 */
export const ROUTE_OPTIONS = [
  { value: 'manual_portal', label: 'Manual (Portal)' },
  { value: 'bulk_import', label: 'Bulk Import (CSV)' },
  { value: 'api', label: 'API' },
];

export const OUTCOME_OPTIONS = [
  { value: 'success', label: 'Success' },
  { value: 'failed', label: 'Failed' },
  { value: 'skipped', label: 'Skipped' },
];

const OperationLogFilter = ({
  search,
  setSearch,
  route,
  setRoute,
  outcome,
  setOutcome,
  setDateRange,
  tempDateRange,
  setTempDateRange,
  showCalendar,
  setShowCalendar,
  calendarFilterTitle,
  setCalendarFilterTitle,
  setTempCalendarFilterTitle,
  handleApply,
}) => {
  const addDays = dateFns.addDays;
  const { datePickerButtonRef, getCalendarPosition } = useDatePickerPosition();

  const handleSelect = (ranges) => {
    const selectedRange = customStaticRanges.find((staticRange) =>
      staticRange.isSelected(ranges.selection),
    );
    setTempCalendarFilterTitle(selectedRange?.label || 'Custom');
    setTempDateRange([ranges.selection]);
  };

  const defaultDateRange = () => [
    {
      endDate: new Date(),
      startDate: addDays(new Date(), -6),
      key: 'selection',
    },
  ];

  /** Calendar footer: puts the date range back, leaves the other filters alone. */
  const handleClear = () => {
    const defaultRange = defaultDateRange();
    setTempCalendarFilterTitle('Last 7 days');
    setCalendarFilterTitle('Last 7 days');
    setTempDateRange(defaultRange);
    setDateRange(defaultRange);
    setShowCalendar(false);
  };

  /** Filter row: clears everything. */
  const handleReset = () => {
    handleClear();
    setSearch('');
    setRoute('');
    setOutcome('');
  };

  // Field metrics are the ones every other list filter uses (EInvoiceListFilter,
  // OrderListFilter, ActivityLogFilter): full width on mobile, 200px from md up,
  // white background, 10px radius, and the control left at its default size.
  const fieldWidth = { base: '100%', md: '200px' };
  const fieldBoxWidth = { base: '100%', md: 'auto' };

  return (
    <Flex
      alignItems="flex-end"
      flexWrap="wrap"
      mb={4}
      gap={{ base: '14px', md: '20px' }}
    >
      <Box width={fieldBoxWidth}>
        <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
          Record or user
        </Text>
        <Input
          placeholder="Search merchant or user"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          width={fieldWidth}
          background="#fff"
          borderRadius="10px"
        />
      </Box>

      <Box width={fieldBoxWidth}>
        <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
          Route
        </Text>
        <Select
          placeholder="All routes"
          value={route}
          onChange={(e) => setRoute(e.target.value)}
          width={fieldWidth}
          background="#fff"
          borderRadius="10px"
        >
          {ROUTE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Box>

      <Box width={fieldBoxWidth}>
        <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
          Outcome
        </Text>
        <Select
          placeholder="All outcomes"
          value={outcome}
          onChange={(e) => setOutcome(e.target.value)}
          width={fieldWidth}
          background="#fff"
          borderRadius="10px"
        >
          {OUTCOME_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
      </Box>

      <Box width={fieldBoxWidth}>
        <Text fontSize="xs" fontWeight="600" mb="1" color="gray.400">
          Date range
        </Text>
        <Box position="relative" flexShrink="0" width={fieldBoxWidth}>
          <Button
            ref={datePickerButtonRef}
            py="10px"
            px="15"
            fontWeight="semibold"
            type="button"
            bg="white"
            border="1px solid"
            borderColor="reviveGray"
            color="#292D32"
            borderRadius="8px"
            onClick={() => setShowCalendar(!showCalendar)}
            justifyContent="space-between"
            width={fieldWidth}
          >
            {calendarFilterTitle}
            <FaChevronDown size={11} style={{ marginLeft: '8px' }} />
          </Button>

          {showCalendar && (
            <>
              {/* Backdrop: closes on an outside click and stops the page
                  scrolling behind the panel on a phone. */}
              <Box
                position="fixed"
                bg="rgb(0 0 0 / 40%)"
                top="0"
                left="0"
                right="0"
                bottom="0"
                zIndex="999"
                onClick={() => setShowCalendar(false)}
              ></Box>
              {/* `position: fixed`, not absolute. `getCalendarPosition` measures
                  the button with `getBoundingClientRect`, which is relative to
                  the viewport - feeding those coordinates to an absolutely
                  positioned box placed it relative to this wrapper instead, so
                  the two-month panel ran off the right edge of the screen. The
                  viewport caps below keep it on screen at every width. */}
              <Box
                p={{ base: '20px', md: '32px' }}
                mt="8px"
                position="fixed"
                {...getCalendarPosition()}
                zIndex="1000"
                bg="white"
                boxShadow="0px 0px 40px rgba(32, 57, 70, 0.06)"
                borderRadius="20px"
                className="custom-date-picker"
                maxWidth={{ base: '95vw', md: '900px' }}
                width={{ base: '95vw', md: 'auto' }}
                maxHeight="90vh"
                overflowY="auto"
                overflowX="hidden"
              >
                <DateRangePicker
                  ranges={tempDateRange}
                  onChange={handleSelect}
                  showSelectionPreview={true}
                  moveRangeOnFirstSelection={false}
                  months={2}
                  direction="horizontal"
                  staticRanges={customStaticRanges}
                  inputRanges={[]}
                  locale={enGB}
                  minDate={new Date(2000, 0, 1)}
                  maxDate={new Date()}
                />
                <Flex justifyContent="right" gap="12px" marginTop="30px">
                  <Button
                    className="clear-btn"
                    border="1px solid"
                    borderColor="reviveGray"
                    borderRadius="10px"
                    onClick={handleClear}
                    bg="white"
                    _hover={{ bg: 'gray.50' }}
                  >
                    Clear
                  </Button>
                  <Button
                    className="apply-btn"
                    bg="reviveOrange"
                    color="white"
                    borderRadius="10px"
                    onClick={handleApply}
                    _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
                  >
                    Apply
                  </Button>
                </Flex>
              </Box>
            </>
          )}
        </Box>
      </Box>

      <Box width={fieldBoxWidth}>
        <Button
          onClick={handleReset}
          bg="gray.100"
          color="gray.700"
          borderRadius="8px"
          width={{ base: '100%', md: 'auto' }}
          _hover={{ bg: 'gray.200' }}
        >
          Reset
        </Button>
      </Box>
    </Flex>
  );
};

OperationLogFilter.propTypes = {
  search: PropTypes.string,
  setSearch: PropTypes.func,
  route: PropTypes.string,
  setRoute: PropTypes.func,
  outcome: PropTypes.string,
  setOutcome: PropTypes.func,
  setDateRange: PropTypes.func,
  tempDateRange: PropTypes.array,
  setTempDateRange: PropTypes.func,
  showCalendar: PropTypes.bool,
  setShowCalendar: PropTypes.func,
  calendarFilterTitle: PropTypes.string,
  setCalendarFilterTitle: PropTypes.func,
  setTempCalendarFilterTitle: PropTypes.func,
  handleApply: PropTypes.func,
};

export default OperationLogFilter;
